/**
 * CCTVLiveWall — multi-camera live grid (Phase A · iter 397).
 *
 * Renders 1×1 / 2×2 / 3×3 / 4×4 grids of online cameras using the existing
 * HLS bridge (`/api/cctv/stream/...`). Browser still cannot play raw RTSP,
 * so each tile reuses `HLSPlayer` which already drives the conversion.
 */
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Grid2X2, Grid3X3, LayoutGrid, Maximize2, RefreshCw, CameraOff } from 'lucide-react';
import HLSPlayer from '@/components/HLSPlayer';

const LAYOUTS = {
  '1x1': { cols: 'grid-cols-1', count: 1, icon: Maximize2 },
  '2x2': { cols: 'grid-cols-2', count: 4, icon: Grid2X2 },
  '3x3': { cols: 'grid-cols-3', count: 9, icon: Grid3X3 },
  '4x4': { cols: 'grid-cols-4', count: 16, icon: LayoutGrid },
};

const StreamTile = ({ device, muted = true, onMaximize }) => {
  const online = !!device?.status?.is_online;
  const streamType = (device?.stream_type || 'rtsp').toLowerCase();
  const cam_id = device?.id || device?._id;
  return (
    <div className="relative bg-black rounded-md overflow-hidden aspect-video group"
         data-testid={`live-wall-tile-${cam_id}`}>
      {online && cam_id ? (
        <HLSPlayer cameraId={cam_id} muted={muted} />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-slate-400">
          <div className="text-center">
            <CameraOff className="h-8 w-8 mx-auto mb-1 opacity-60" />
            <div className="text-xs">{online ? 'No stream' : 'Offline'}</div>
          </div>
        </div>
      )}
      {/* Overlay */}
      <div className="absolute top-1 left-1 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm rounded px-1.5 py-0.5">
        <span className={`h-1.5 w-1.5 rounded-full ${online ? 'bg-emerald-400 animate-pulse' : 'bg-rose-400'}`} />
        <span className="text-[10px] text-white truncate max-w-[120px]">{device?.name}</span>
      </div>
      <Button size="icon" variant="ghost"
               onClick={() => onMaximize && onMaximize(device)}
               className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition bg-black/40 text-white h-7 w-7"
               data-testid={`live-wall-maximize-${cam_id}`}>
        <Maximize2 className="h-3.5 w-3.5" />
      </Button>
      {device?.location && (
        <div className="absolute bottom-1 left-1 text-[10px] text-white/80 bg-black/40 rounded px-1.5 py-0.5 truncate max-w-[80%]">
          {device.location}
        </div>
      )}
    </div>
  );
};

export const CCTVLiveWall = ({ devices = [], onMaximize, onRefresh }) => {
  const [layout, setLayout] = useState('2x2');
  const [muted, setMuted] = useState(true);
  const L = LAYOUTS[layout];

  // Show online first, then offline; cap to the layout's slot count.
  const tiles = useMemo(() => {
    const sorted = [...devices].sort((a, b) =>
      (b?.status?.is_online ? 1 : 0) - (a?.status?.is_online ? 1 : 0));
    return sorted.slice(0, L.count);
  }, [devices, L.count]);

  // Pad empty slots so the grid is visually complete
  const emptySlots = Math.max(0, L.count - tiles.length);

  return (
    <Card data-testid="cctv-live-wall">
      <CardContent className="pt-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="text-sm text-muted-foreground">
            Showing {tiles.length} of {devices.length} cameras
          </div>
          <div className="flex items-center gap-2">
            {Object.entries(LAYOUTS).map(([k, v]) => {
              const Icon = v.icon;
              return (
                <Button key={k} size="sm" variant={layout === k ? 'default' : 'outline'}
                         onClick={() => setLayout(k)}
                         data-testid={`live-wall-layout-${k}`}>
                  <Icon className="h-3.5 w-3.5 mr-1" /> {k}
                </Button>
              );
            })}
            <Button size="sm" variant="outline" onClick={() => setMuted(m => !m)}
                     data-testid="live-wall-mute">
              {muted ? 'Unmute' : 'Mute'}
            </Button>
            {onRefresh && (
              <Button size="icon" variant="ghost" onClick={onRefresh} className="h-8 w-8"
                       data-testid="live-wall-refresh">
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {tiles.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <CameraOff className="h-10 w-10 mx-auto mb-2 opacity-50" />
            No cameras to display. Add devices from the Devices tab.
          </div>
        ) : (
          <div className={`grid ${L.cols} gap-2`}>
            {tiles.map(d => (
              <StreamTile key={d.id || d._id} device={d}
                            muted={muted} onMaximize={onMaximize} />
            ))}
            {Array.from({ length: emptySlots }).map((_, i) => (
              <div key={`empty-${i}`}
                   className="aspect-video bg-slate-100 dark:bg-slate-900/40 border border-dashed border-slate-300 dark:border-slate-700 rounded-md flex items-center justify-center text-slate-400 text-xs">
                Empty
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CCTVLiveWall;
